/**
 * AdminRoiMetrics — CaraLoom command center · verified pilot metrics (iter 179).
 *
 * Wires the ROI card metric slots on the public homepage (`/`) and the
 * hospitals landing page (`/discharge-planning-software`) to a small
 * admin form. When published, verified pilot numbers replace the
 * qualitative-only rendering — no code change, no deploy.
 *
 * Route: /admin/roi-metrics (admin-gated via ProtectedRoute).
 * Backend: GET /api/admin/roi-metrics + PUT /api/admin/roi-metrics
 *          GET /api/public/roi-metrics (unauth)
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Save, Loader2, Eye, EyeOff, Clock, LineChart, BadgeCheck,
  Shield, ShieldCheck, Inbox, Building2, Users, Map, Gauge,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADMIN_NAV = [
  { icon: Shield,      label: 'Compliance',       href: '/admin/compliance' },
  { icon: ShieldCheck, label: 'AI Validation',    href: '/admin/ai-validation' },
  { icon: Inbox,       label: 'Intake Inbox',     href: '/admin/intake' },
  { icon: Building2,   label: 'Referral Sources', href: '/admin/referral-sources' },
  { icon: Users,       label: 'CRM',              href: '/admin/crm' },
  { icon: ShieldCheck, label: 'HIE Console',      href: '/admin/hie' },
  { icon: Map,         label: 'Network Map',      href: '/admin/network-map' },
  { icon: Gauge,       label: 'ROI Metrics',      href: '/admin/roi-metrics', active: true },
];

// Card slots — one row per public card. Match the four homepage / hospitals
// ROI cards exactly.
const CARD_SLOTS = [
  { key: 'faster_placement',       title: 'Faster placement',       icon: Clock,      body: 'Reduce the time between referral and confirmed acceptance.', example: '42 min · Median referral → acceptance' },
  { key: 'fewer_manual_touches',   title: 'Fewer manual touches',   icon: LineChart,  body: 'Less time chasing status, calls, and faxes.',              example: '63% · Reduction in manual touches' },
  { key: 'greater_visibility',     title: 'Greater visibility',     icon: Eye,        body: 'Case management sees where every discharge stands.',       example: '94% · Placement outcomes visible' },
  { key: 'measurable_performance', title: 'Measurable performance', icon: BadgeCheck, body: 'Transition metrics reported by service line and unit.',    example: '18 units · Transitions measured' },
];

export default function AdminRoiMetrics() {
  const { getAuthHeader } = useAuth();
  const [rows, setRows] = useState(() =>
    Object.fromEntries(CARD_SLOTS.map((c) => [c.key, { value: '', caption: '' }])));
  const [published, setPublished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [updatedBy, setUpdatedBy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/roi-metrics`, getAuthHeader());
      setPublished(!!data.published);
      setUpdatedAt(data.updated_at || null);
      setUpdatedBy(data.updated_by || null);
      const next = Object.fromEntries(CARD_SLOTS.map((c) => [c.key, { value: '', caption: '' }]));
      (data.cards || []).forEach((c) => {
        if (next[c.key]) next[c.key] = { value: c.value || '', caption: c.caption || '' };
      });
      setRows(next);
    } catch (e) {
      toast.error('Could not load ROI metrics. Check your admin role.');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);

  const save = async (nextPublished = published) => {
    setSaving(true);
    try {
      const cards = CARD_SLOTS.map((c) => ({
        key: c.key,
        value: rows[c.key].value.trim() || null,
        caption: rows[c.key].caption.trim() || null,
      }));
      const { data } = await axios.put(`${API}/admin/roi-metrics`,
        { published: !!nextPublished, cards },
        getAuthHeader(),
      );
      setPublished(!!data.published);
      setUpdatedAt(data.updated_at || null);
      toast.success(nextPublished ? 'Published. Public pages will pick it up on next load.' : 'Saved as draft.');
    } catch (e) {
      toast.error('Save failed. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const setField = (key, field, val) => {
    setRows((s) => ({ ...s, [key]: { ...s[key], [field]: val } }));
  };

  return (
    <DashboardShell navItems={ADMIN_NAV}>
      <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="admin-roi-metrics-page">
        <header className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-cn-text tracking-tight flex items-center gap-2">
              <Gauge className="w-5 h-5 text-cn-primary dark:text-cn-accent" /> ROI Metrics
            </h1>
            <p className="text-sm text-cn-text-muted mt-1 max-w-2xl">
              Verified pilot numbers surface here on the homepage and hospitals landing page ROI cards.
              Leave any card blank to keep the qualitative-only rendering. Metrics only appear publicly
              when <b>Published</b> is on.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest border ${
                published
                  ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40'
                  : 'bg-cn-bone-100 text-cn-text-muted border-cn-border'
              }`}
              data-testid="roi-published-badge"
            >
              {published ? <><Eye className="w-3 h-3" /> Published</> : <><EyeOff className="w-3 h-3" /> Draft</>}
            </span>
          </div>
        </header>

        {loading ? (
          <div className="py-16 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-cn-text-muted" />
          </div>
        ) : (
          <>
            <div className="grid gap-4">
              {CARD_SLOTS.map((c) => (
                <div
                  key={c.key}
                  className="rounded-2xl border border-cn-border bg-cn-bg p-5"
                  data-testid={`roi-card-editor-${c.key}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-none w-11 h-11 rounded-2xl bg-cn-primary/10 dark:bg-cn-accent/15 text-cn-primary dark:text-cn-accent flex items-center justify-center">
                      <c.icon className="w-5 h-5" strokeWidth={1.8} />
                    </div>
                    <div className="flex-1">
                      <h3 className="cn-display text-lg text-cn-text">{c.title}</h3>
                      <p className="text-xs text-cn-text-muted mt-0.5">{c.body}</p>
                      <p className="text-[10px] text-cn-text-muted mt-1 italic">Example — {c.example}</p>

                      <div className="mt-4 grid md:grid-cols-[220px_1fr] gap-3">
                        <div>
                          <label htmlFor={`v-${c.key}`} className="text-[10px] uppercase tracking-widest text-cn-text-muted font-mono block mb-1">
                            Headline value
                          </label>
                          <input
                            id={`v-${c.key}`}
                            type="text"
                            value={rows[c.key].value}
                            onChange={(e) => setField(c.key, 'value', e.target.value)}
                            placeholder="e.g. 42 min"
                            maxLength={32}
                            className="w-full rounded-lg border border-cn-border bg-cn-bg px-3 py-2 text-lg font-semibold text-cn-primary dark:text-cn-accent tabular-nums"
                            data-testid={`roi-input-value-${c.key}`}
                          />
                        </div>
                        <div>
                          <label htmlFor={`c-${c.key}`} className="text-[10px] uppercase tracking-widest text-cn-text-muted font-mono block mb-1">
                            Caption
                          </label>
                          <input
                            id={`c-${c.key}`}
                            type="text"
                            value={rows[c.key].caption}
                            onChange={(e) => setField(c.key, 'caption', e.target.value)}
                            placeholder="Median referral → acceptance"
                            maxLength={120}
                            className="w-full rounded-lg border border-cn-border bg-cn-bg px-3 py-2 text-sm text-cn-text"
                            data-testid={`roi-input-caption-${c.key}`}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl border border-cn-border bg-cn-bone-100/60 dark:bg-cn-ink-900/40 p-5">
              <div className="flex items-center gap-3 flex-wrap">
                <label className="inline-flex items-center gap-2 text-sm text-cn-text" htmlFor="roi-publish-toggle">
                  <input
                    id="roi-publish-toggle"
                    type="checkbox"
                    checked={published}
                    onChange={(e) => setPublished(e.target.checked)}
                    data-testid="roi-publish-toggle"
                  />
                  <span className="font-semibold">Publish to public pages</span>
                </label>
                <span className="text-xs text-cn-text-muted">
                  When on, the homepage and hospitals landing show the values above under each ROI card.
                </span>
              </div>
              <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
                <div className="text-[11px] text-cn-text-muted">
                  {updatedAt ? <>Last updated <b>{new Date(updatedAt).toLocaleString()}</b>{updatedBy ? <> by {updatedBy}</> : null}.</> : 'No saves yet.'}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => save(published)}
                    disabled={saving}
                    className="cn-btn cn-btn-primary text-sm"
                    data-testid="roi-save-btn"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save</>}
                  </button>
                </div>
              </div>
            </div>

            <p className="text-[11px] text-cn-text-muted italic">
              These numbers appear as headline metrics under each ROI card on `/` and `/discharge-planning-software`.
              Every card is optional. Leave the headline value blank to render the card in qualitative-only mode.
            </p>
          </>
        )}
      </div>
    </DashboardShell>
  );
}
